import React, { ReactElement } from "react";
import { ConversationSource, useGetHolidayCountriesQuery } from "../../../__generated__/api";
import Select, { Option, SelectInstanceHandler } from "../../select/Select";

export interface HolidayCountryProps<TIsMulti = false, TIsClearable=false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    isClearable?: TIsClearable;
    placeholder?: string;
    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (
        sourceId: (TIsMulti extends true ? string[] : string) | (TIsClearable extends true ? undefined : never),
    ) => void;
}

const HolidayCountries = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, isClearable,
}: HolidayCountryProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useGetHolidayCountriesQuery();

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<ConversationSource>[] : Option<ConversationSource>;
    type ValueType = TIsMulti extends true ? Option<ConversationSource>[] : Option<ConversationSource>;

    const handleSelected = (selected: SelectedType) => {
        if (!selected) {
            (onSelected as (empty: unknown) => void)(undefined);
            return;
        }
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<ConversationSource>).value) as Parameters<typeof onSelected>[0];
        onSelected(values);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const options = data?.holidayCountries?.map(country => ({
        label: country.name as string,
        value: country.id as string,
    })) ?? [];
    const optionMap = options.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => optionMap[row]) : optionMap[value as string];
    return (
        <Select
            ref={ref}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            options={options}
            value={selected as ValueType}
            isClearable={isClearable}
        />
    );
});

HolidayCountries.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    isClearable: undefined,
};

export default HolidayCountries as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false
> (
    props: HolidayCountryProps<TMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof HolidayCountries>;
